import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { api, errMsg, dt, today } from "@/lib/api";
import { PageHeader, Card, Table, Field, Sel, Tag } from "@/components/common";
import { Modal, SaveBtn, NewBtn } from "@/components/Modal";
import { useAuth } from "@/context/AuthContext";
import { ReminderDetail } from "@/components/ReminderDetail";

const DAYS = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];
const REPEAT = [{ value: "once", label: "Sekali" }, { value: "daily", label: "Setiap hari" }, { value: "weekly", label: "Hari tertentu tiap minggu" }, { value: "monthly", label: "Setiap bulan (tanggal tertentu)" }];

const Targets = ({ value, onChange }) => {
  const [opts, setOpts] = useState([]);
  useEffect(() => { api.get("/reminders/targets").then((r) => setOpts(r.data)); }, []);
  const tog = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="border rounded-md max-h-44 overflow-y-auto divide-y" data-testid="reminder-targets">
      {opts.map((o) => (
        <label key={o.id} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50">
          <input type="checkbox" checked={value.includes(o.id)} onChange={() => tog(o.id)} data-testid={`reminder-target-${o.id}`} />
          <span className="font-medium">{o.name}</span><span className="text-xs text-slate-400">{o.role}</span>
        </label>
      ))}
    </div>
  );
};

const ReminderForm = ({ rm, onClose }) => {
  const { user } = useAuth();
  const [f, setF] = useState({ title: "", note: "", link: "", repeat: "once", date: today(), days: [], monthday: 1, time: "09:00", target_user_ids: [user.id], active: true, ...rm });
  const save = async () => {
    const body = { ...f, monthday: f.repeat === "monthly" ? Number(f.monthday) : null };
    try { f.id ? await api.put(`/reminders/${f.id}`, body) : await api.post("/reminders", body); toast.success("Pengingat disimpan"); onClose(true); } catch (e) { toast.error(errMsg(e)); }
  };
  const tog = (d) => setF({ ...f, days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d] });
  return (
    <Modal open onOpenChange={() => onClose(false)} title={f.id ? `Ubah pengingat ${f.code}` : "Pengingat baru"} testid="reminder-modal">
      <div className="space-y-3">
        <Field label="Judul"><input className="inp" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="reminder-title-input" /></Field>
        <Field label="Catatan"><textarea className="inp min-h-[60px]" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="reminder-note-input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Pengulangan"><Sel testid="reminder-repeat-select" value={f.repeat} onChange={(v) => setF({ ...f, repeat: v })} options={REPEAT} /></Field>
          <Field label="Jam (WIB)"><input type="time" className="inp num" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} data-testid="reminder-time-input" /></Field>
        </div>
        {f.repeat === "once" && <Field label="Tanggal"><input type="date" className="inp" value={f.date || ""} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="reminder-date-input" /></Field>}
        {f.repeat === "weekly" && (
          <Field label="Hari">
            <div className="flex flex-wrap gap-1.5">{DAYS.map((d, i) => (
              <button key={d} type="button" onClick={() => tog(i)} data-testid={`reminder-day-${i}`}
                className={`h-8 w-12 rounded-md text-xs font-semibold border transition-colors ${f.days.includes(i) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white hover:bg-slate-50"}`}>{d}</button>
            ))}</div>
          </Field>
        )}
        {f.repeat === "monthly" && (
          <Field label="Tanggal tiap bulan">
            <Sel testid="reminder-monthday-select" value={String(f.monthday || 1)} onChange={(v) => setF({ ...f, monthday: Number(v) })} options={Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: `Tanggal ${i + 1}` }))} />
            {f.monthday > 28 && <div className="text-xs text-slate-500 mt-1">Bulan yang lebih pendek memakai tanggal terakhir bulan itu.</div>}
          </Field>
        )}
        <Field label="Untuk"><Targets value={f.target_user_ids} onChange={(v) => setF({ ...f, target_user_ids: v })} /></Field>
        <Field label="Tautan halaman (opsional, mis. /opname/new)"><input className="inp" value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })} data-testid="reminder-link-input" /></Field>
        {f.id && <Field label="Status"><Sel testid="reminder-active-select" value={String(f.active)} onChange={(v) => setF({ ...f, active: v === "true" })} options={[{ value: "true", label: "Aktif" }, { value: "false", label: "Nonaktif" }]} /></Field>}
        <SaveBtn onClick={save} testid="reminder-save-button" />
      </div>
    </Modal>
  );
};

export const markDone = async (id, after) => {
  try { await api.post(`/reminders/${id}/done`); toast.success("Pengingat ditandai selesai"); after(); } catch (e) { toast.error(errMsg(e)); }
};

const statusOf = (r) => !r.active ? <Tag>Nonaktif</Tag> : r.due ? <Tag tone="amber">Jatuh tempo</Tag> : r.next_at ? <Tag tone="emerald">Terjadwal</Tag> : <Tag tone="slate">Selesai</Tag>;

export default function Reminders() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [view, setView] = useState(null);
  const load = () => api.get("/reminders").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div>
      <PageHeader title="Pengingat" sub="Pengingat sekali atau berulang, tampil di Tindakan mendesak saat waktunya tiba.">
        <NewBtn onClick={() => setEdit({})} testid="new-reminder-button">Pengingat baru</NewBtn>
      </PageHeader>
      <Card>
        <Table testid="reminders-table" rows={rows} onRow={(r) => setView(r)} empty="Belum ada pengingat" cols={[
          { h: "Judul", r: (r) => <div><div className="font-semibold">{r.title}</div>{r.note && <div className="text-xs text-slate-500">{r.note}</div>}</div> },
          { h: "Jadwal", r: (r) => <span className="text-xs">{r.schedule_text}</span> },
          { h: "Untuk", r: (r) => <span className="text-xs">{r.targets.map((t) => `${t.name}${r.done_by.includes(t.id) ? " ✓" : ""}`).join(", ")}</span> },
          { h: "Dibuat oleh", r: (r) => <span className="text-xs">{r.created_by.name}</span> },
          { h: "Berikutnya", r: (r) => <span className="text-xs text-slate-500">{dt(r.next_at)}</span> },
          { h: "Status", r: statusOf },
          { h: "", right: true, r: (r) => r.due && <button onClick={(e) => { e.stopPropagation(); markDone(r.id, load); }} data-testid={`reminder-done-${r.id}`}
            className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700"><Check size={13} /> Selesai</button> },
        ]} />
      </Card>
      {view && <ReminderDetail rm={view} onClose={() => setView(null)} onEdit={() => { setEdit(view); setView(null); }} />}
      {edit && <ReminderForm rm={edit.id ? edit : undefined} onClose={(ok) => { setEdit(null); ok && load(); }} />}
    </div>
  );
}
